import { PlaceholderSvg1 } from '@eventbrite/eds-iconography';
import { Illustration } from '@eventbrite/eds-illustration';
import { gettext } from '@eventbrite/i18n';
import React, { CSSProperties, useState } from 'react';
import './EventCardImage.scss';

export interface EventImageProps {
    src?: string;
    name: string;
    tokens?: EventImageTokens;
    loading?: 'eager' | 'lazy';
    shouldCompressImg?: boolean;
    imageSizes?: {
        small?: string;
        medium?: string;
        large?: string;
    };
    preferredSize?: 'small' | 'medium' | 'large';
}

export interface EventImageTokens {
    '--image-aspect-ratio'?: string;
    '--image-aspect-ratio-mobile'?: string;
    '--image-width'?: string;
    '--image-width-mobile'?: string;
    '--image-background-color'?: string;
    '--image-border-radius'?: string;
    '--image-object-position'?: string;
}

//Hint to the browser of the actual
//image asset size for faster loading
const STATIC_IMAGE_HEIGHT = 256;
const STATIC_IMAGE_WIDTH = 512;

// Mobile: full viewport minus typical padding. Desktop: ~400px card width.
const DEFAULT_SIZES = '(max-width: 479px) calc(100vw - 48px), 400px';

const buildSrcSet = (imageSizes?: EventImageProps['imageSizes']) => {
    if (!imageSizes) return undefined;
    const entries: string[] = [];
    if (imageSizes.small) entries.push(`${imageSizes.small} 320w`);
    if (imageSizes.medium) entries.push(`${imageSizes.medium} 640w`);
    if (imageSizes.large) entries.push(`${imageSizes.large} 1024w`);
    return entries.length > 0 ? entries.join(', ') : undefined;
};

export const getEventImageAltContent = (eventName: string) =>
    gettext('%(eventName)s primary image', { eventName });

/**
 * `EventCardImage`
 *
 * Component used to render images within an EventCard. Use defined
 * tokens to configure for your particular usecase.
 *
 * Tokens:
 *
 * `--ImageAspectRatio`
 *
 * `--ImageAspectRatioMobile`
 *
 * `--ImageWidth`
 *
 * `--ImageWidthMobile`
 *
 * `--ImageBorderRadius`
 *
 * `--ImageBackgroundColor`
 *
 * @param props
 * @returns
 */
export const EventCardImage: React.FunctionComponent<EventImageProps> = (
    props,
) => {
    const alt = getEventImageAltContent(props.name);
    const [hasImageError, setHasImageError] = useState(false);

    const handleImageError = () => {
        setHasImageError(true);
    };

    const getOptimalImageSrc = () => {
        if (!props.imageSizes) {
            return props.src;
        }

        const preferredSize = props.preferredSize || 'medium';

        if (props.imageSizes[preferredSize]) {
            return props.imageSizes[preferredSize];
        }

        return (
            props.imageSizes.medium ||
            props.imageSizes.large ||
            props.imageSizes.small ||
            props.src
        );
    };

    const imageSrc = getOptimalImageSrc();
    const srcSet = buildSrcSet(props.imageSizes);

    return (
        <div
            className="event-card-image__aspect-container"
            style={{ ...props.tokens } as CSSProperties}
            data-testid="event-card-image-container"
        >
            {imageSrc && !hasImageError ? (
                <img
                    height={STATIC_IMAGE_HEIGHT}
                    width={STATIC_IMAGE_WIDTH}
                    className="event-card-image"
                    src={imageSrc}
                    srcSet={srcSet}
                    sizes={srcSet ? DEFAULT_SIZES : undefined}
                    loading={props.loading}
                    alt={alt.toString()}
                    key={imageSrc}
                    onError={handleImageError}
                    //HTML Img Definition doesnt support fetchpriority but it
                    //is supported in chrome and edge with broader support coming
                    //https://caniuse.com/mdn-html_elements_img_fetchpriority
                    //eslint-disable-next-line
                    //@ts-ignore
                    fetchpriority={props.shouldCompressImg ? 'high' : undefined}
                />
            ) : (
                <div className="event-card-image">
                    <Illustration
                        type={<PlaceholderSvg1 />}
                        height="100%"
                        width="100%"
                    />
                </div>
            )}
        </div>
    );
};
